import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, vModelRadio as _vModelRadio, withDirectives as _withDirectives, normalizeClass as _normalizeClass, mergeProps as _mergeProps, createBlock as _createBlock } from "vue"

const _hoisted_1 = { id: "healthcaredata02-03-b-modal" }
const _hoisted_2 = { class: "modal modal-select-area" }
const _hoisted_3 = { class: "modal-header" }
const _hoisted_4 = { class: "modal-header-title" }
const _hoisted_5 = { class: "modal-body" }
const _hoisted_6 = {
  key: 0,
  class: "select-area-form step-1"
}
const _hoisted_7 = { class: "select-area-form-steps" }
const _hoisted_8 = { class: "select-area-form-step is-active" }
const _hoisted_9 = { class: "select-area-form-step-title" }
const _hoisted_10 = { class: "select-area-form-step" }
const _hoisted_11 = { class: "select-area-form-step-title" }
const _hoisted_12 = { class: "select-area-form-prefecture" }
const _hoisted_13 = ["id"]
const _hoisted_14 = ["for"]
const _hoisted_15 = ["id"]
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  key: 1,
  class: "select-area-form step-2"
}
const _hoisted_18 = { class: "select-area-form-steps" }
const _hoisted_19 = { class: "select-area-form-step is-done" }
const _hoisted_20 = { class: "select-area-form-step-title" }
const _hoisted_21 = { class: "select-area-form-step is-active" }
const _hoisted_22 = { class: "select-area-form-step-title" }
const _hoisted_23 = ["alt"]
const _hoisted_24 = { class: "select-area-form-city" }
const _hoisted_25 = ["id"]
const _hoisted_26 = ["for"]
const _hoisted_27 = ["id"]
const _hoisted_28 = ["id", "value"]
const _hoisted_29 = ["for"]
const _hoisted_30 = { class: "modal-footer" }
const _hoisted_31 = { class: "modal-footer-buttons" }
const _hoisted_32 = ["disabled"]

import { ref, computed, onMounted, reactive } from "vue";
import { DiagnosisProcess } from "@/models/myRecord/diagnosisProcess";
import ErrorDialog from "@/views/base/CommonMessage.vue";
import { processing, processend } from "@/utils/view";
import code from "@/constants/code";
import { _$t } from "@/utils/i18n";

// ---- Props & Emits -----------------------------------------
interface Props {
  title: string;
}

// eslint-disable-next-line no-undef
type Prefecture = {
  code: string;
  name: string;
};

type PrefectureRegion = {
  name: string;
  prefectures: Prefecture[];
};

type City = {
  name: string;
  value: string;
};

type CityGroup = {
  name: string;
  cities: City[];
};

// DPF get-municipalities のレスポンス1件（バックエンド共通形式）
type DpfMunicipalityItem = {
  code?: number;
  prefecture_code?: number;
  name: string;
  city?: string | null;
  ward?: string | null;
};

type DpfMunicipalityGroup = {
  isDesigned?: boolean;
  city: string | null;
  code: number | null;
  data: DpfMunicipalityItem[];
};

// ---- Variable ----------------------------------------------

export default /*@__PURE__*/_defineComponent({
  __name: 'SelectAreaModal',
  props: {
    title: {}
  },
  emits: ["close", "select"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// eslint-disable-next-line no-undef
const emits = __emit;

// ---- Types -------------------------------------------------
const currentStep = ref(1);
const selectedPrefecture = ref<Prefecture | null>(null);
const selectedCityName = ref("");

const modalTitle = computed(() => props.title);

// 都道府県データ（DPF API取得後、クライアントで地域別にグループ化）
const prefectureRegions = ref<PrefectureRegion[]>([]);

const regionDefs =
  (code.attributes.prefectureRegions as Array<{
    name: string;
    minCode: number;
    maxCode: number;
  }>) ?? [];

function buildPrefectureRegions(
  list: Array<{ code: string | number; name: string }>
): PrefectureRegion[] {
  return regionDefs.map((region) => {
    const prefectures = list
      .filter((p) => {
        const n =
          typeof p.code === "number" ? p.code : parseInt(String(p.code), 10);
        return !Number.isNaN(n) && n >= region.minCode && n <= region.maxCode;
      })
      .map((p) => ({
        code: String(p.code).padStart(2, "0"),
        name: p.name,
      }));
    return { name: region.name, prefectures };
  });
}

/**
 * バックエンド municipalities を表示用 cityGroups に変換（仕様に従う）
 * 1.東京: 東京23区内 / 東京23区外
 * 2.政令指定都市なし: アコーディオン不要のフラット一覧
 * 3.政令指定都市あり: 政令指定都市ごと + その他の市町村
 */
function dpfMunicipalitiesToCityGroups(
  groups: DpfMunicipalityGroup[],
  prefCode: number,
  t: (key: string) => string
): { cityGroups: CityGroup[]; isFlatList: boolean } {
  const toCity = (name: string, value?: string): City => ({
    name,
    value: value ?? name,
  });

  // 1. 東京都（prefCode 13）: 区と市は同階層。区＝「東京23区内」、それ以外（市・町村）＝「東京23区外」
  // APIでは区も市も g.city に名前、data は空。町村は g.city=null で g.data[].name に名前。
  if (prefCode === 13) {
    const in23: City[] = []; // 東京23区（名前が「区」で終わるもの）
    const out23: City[] = []; // 東京23区外（市・町村）

    for (const g of groups) {
      if (g.city != null && g.city !== "") {
        // 区 or 市（同階層で city に名前が入る）
        if (/区$/.test(g.city)) {
          in23.push(toCity(g.city));
        } else {
          out23.push(toCity(g.city));
        }
      } else {
        // 町村（city: null、data に name が入る）
        for (const r of g.data || []) {
          if (r.name) out23.push(toCity(r.name));
        }
      }
    }

    const cityGroups: CityGroup[] = [];
    if (in23.length > 0) {
      cityGroups.push({
        name: t("diagnosisProcess_selectArea_tokyo_23_in"),
        cities: in23,
      });
    }
    if (out23.length > 0) {
      cityGroups.push({
        name: t("diagnosisProcess_selectArea_tokyo_23_out"),
        cities: out23,
      });
    }
    return { cityGroups, isFlatList: false };
  }

  const hasDesignated = groups.some((g) => g.isDesigned === true);

  // 2. 政令指定都市がない場合: アコーディオン不要で全市町村をフラットに（市は g.city、町村は g.data[].name）
  if (!hasDesignated) {
    const allCities: City[] = [];
    for (const g of groups) {
      if (g.city != null && g.city !== "") {
        allCities.push(toCity(g.city));
      } else {
        for (const r of g.data || []) {
          if (r.name) allCities.push(toCity(r.name));
        }
      }
    }
    return {
      cityGroups: [{ name: "", cities: allCities }],
      isFlatList: true,
    };
  }

  // 3. 政令指定都市が1つ以上: 政令指定都市ごと（区名は ward）+ その他の市町村（市は g.city、町村は g.data[].name）
  const result: CityGroup[] = [];
  const otherItems: City[] = [];

  for (const g of groups) {
    if (g.isDesigned === true && g.city) {
      // 政令指定都市: アコーディオン内は区名のみ表示（ward。なければ name）
      const cities = (g.data || []).map((r) => {
        const wardName = r.ward != null && r.ward !== "" ? r.ward : r.name;
        // 区は表示名を「区名」のままにしつつ、保存値は「市名+区名」にする
        return toCity(wardName, `${g.city}${wardName}`);
      });
      result.push({ name: g.city, cities });
    } else {
      // その他の市町村: 市（g.city）と町村（g.city=null のとき g.data[].name）の両方を含める
      if (g.city != null && g.city !== "") {
        otherItems.push(toCity(g.city));
      } else {
        for (const r of g.data || []) {
          if (r.name) otherItems.push(toCity(r.name));
        }
      }
    }
  }
  if (otherItems.length > 0) {
    result.push({
      name: t("diagnosisProcess_selectArea_other_municipalities"),
      cities: otherItems,
    });
  }
  return { cityGroups: result, isFlatList: false };
}

// 市区町村データ（APIから取得・変換後）
const cityGroups = ref<CityGroup[]>([]);
// 政令指定都市がない都道府県ではアコーディオンを使わずフラット一覧表示
const isFlatCityList = ref(false);

// エラーダイアログ関連情報
const sessionErr = ref(false);
let errorDialog = reactive({
  show: false,
  okButtonHide: true,
  safe: false,
  message: "",
  open: (message: string): void => {
    processing(); // オーバレイを表示
    errorDialog.message = message;
    errorDialog.show = true;
  },
  close: (): void => {
    processend(); // オーバレイを非表示
    errorDialog.show = false;
    errorDialog.message = "";
    if (sessionErr.value) {
      window.location.replace("/cms/login");
    }
  },
});

// ----- Function -----------------------------------------------
// 都道府県一覧をDPF共通APIで取得し、地域別にグループ化
const fetchPrefectures = async () => {
  const diagnosisProcessModel = new DiagnosisProcess();
  const [_model, response, err] = await diagnosisProcessModel.fetchPrefecture();

  if (err) {
    const decodedMessage = decodeURIComponent(
      err.error?.response?.data?.message
    );
    const status = err.error?.response?.status;
    if (status === 408) {
      // セッションエラー
      sessionErr.value = true;
    }
    errorDialog.open(decodedMessage);
    return;
  }

  const list = response?.data?.prefecture ?? [];
  const arr = Array.isArray(list) ? list : [];
  prefectureRegions.value = buildPrefectureRegions(arr);
};

// 都道府県を選択してStep2へ進む
const selectPrefecture = async (prefecture: Prefecture) => {
  selectedPrefecture.value = prefecture;
  selectedCityName.value = "";
  cityGroups.value = [];
  isFlatCityList.value = false;

  try {
    const diagnosisProcessModel = new DiagnosisProcess();
    // prefCode は数値で渡す（DPF API）
    const prefCodeNum =
      typeof prefecture.code === "string"
        ? parseInt(prefecture.code, 10)
        : prefecture.code;
    const [_model, response, err] = await diagnosisProcessModel.fetchCity(
      String(Number.isNaN(prefCodeNum) ? prefecture.code : prefCodeNum)
    );

    if (err) {
      cityGroups.value = [];
      isFlatCityList.value = false;
      return;
    }

    const raw = response?.data?.municipalities;
    const arr = Array.isArray(raw) ? raw : [];
    const { cityGroups: next, isFlatList } = dpfMunicipalitiesToCityGroups(
      arr,
      prefCodeNum,
      _$t
    );
    cityGroups.value = next;
    isFlatCityList.value = isFlatList;
  } catch {
    cityGroups.value = [];
    isFlatCityList.value = false;
  } finally {
    currentStep.value = 2;
  }
};

// Step1へ戻る
const goToStep1 = () => {
  currentStep.value = 1;
  selectedPrefecture.value = null;
  selectedCityName.value = "";
};

// 決定ボタン押下
const confirmSelection = () => {
  if (!selectedPrefecture.value || !selectedCityName.value) return;

  const locationString = `${selectedPrefecture.value.name} ${selectedCityName.value}`;
  emits(
    "select",
    locationString,
    selectedPrefecture.value.code,
    selectedCityName.value
  );
};

onMounted(() => {
  fetchPrefectures();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[13] || (_cache[13] = _createElementVNode("div", { class: "modal-overlay" }, null, -1)),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, _toDisplayString(modalTitle.value), 1),
          _createElementVNode("button", {
            type: "button",
            class: "modal-close",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
          }, _cache[3] || (_cache[3] = [
            _createElementVNode("img", { src: "/server/necs/images/close-btn.png" }, null, -1)
          ]))
        ]),
        _createElementVNode("div", _hoisted_5, [
          (currentStep.value === 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "select-area-form-step-icon" }, [
                      _createElementVNode("span", null, "1")
                    ], -1)),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(_$t)("diagnosisProcess_selectArea_step_prefecture")), 1)
                  ]),
                  _cache[6] || (_cache[6] = _createElementVNode("div", { class: "select-area-form-separator" }, null, -1)),
                  _createElementVNode("div", _hoisted_10, [
                    _cache[5] || (_cache[5] = _createElementVNode("div", { class: "select-area-form-step-icon" }, [
                      _createElementVNode("span", null, "2")
                    ], -1)),
                    _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(_$t)("diagnosisProcess_selectArea_step_city")), 1)
                  ])
                ]),
                _createElementVNode("ul", _hoisted_12, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prefectureRegions.value, (region, index) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: index,
                      class: "select-area-form-prefecture-item"
                    }, [
                      _createElementVNode("input", {
                        type: "checkbox",
                        class: "prefecture-item-switch",
                        id: `prefecture-item-switch-${index}`
                      }, null, 8, _hoisted_13),
                      _createElementVNode("label", {
                        class: "prefecture-item-label",
                        for: `prefecture-item-switch-${index}`
                      }, [
                        _createElementVNode("span", null, _toDisplayString(region.name), 1),
                        _cache[7] || (_cache[7] = _createElementVNode("img", { src: "/server/necs/images/open_24dp_FILL0_wght600_GRAD0_opsz24.png" }, null, -1))
                      ], 8, _hoisted_14),
                      _createElementVNode("ul", {
                        class: "prefecture-item-list",
                        id: `prefecture-item-list-${index}`
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(region.prefectures, (prefecture) => {
                          return (_openBlock(), _createElementBlock("li", {
                            key: prefecture.code,
                            class: "prefecture-item-row"
                          }, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "prefecture-item-button",
                              onClick: ($event: any) => (selectPrefecture(prefecture))
                            }, [
                              _createTextVNode(_toDisplayString(prefecture.name) + " ", 1),
                              _cache[8] || (_cache[8] = _createElementVNode("img", { src: "/server/necs/images/icon_arrow_right_EC7400.svg" }, null, -1))
                            ], 8, _hoisted_16)
                          ]))
                        }), 128))
                      ], 8, _hoisted_15)
                    ]))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true),
          (currentStep.value === 2)
            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("div", _hoisted_19, [
                    _cache[9] || (_cache[9] = _createElementVNode("div", { class: "select-area-form-step-icon" }, [
                      _createElementVNode("span", null, "1")
                    ], -1)),
                    _createElementVNode("span", _hoisted_20, _toDisplayString(_unref(_$t)("diagnosisProcess_selectArea_step_prefecture")), 1)
                  ]),
                  _cache[11] || (_cache[11] = _createElementVNode("div", { class: "select-area-form-separator" }, null, -1)),
                  _createElementVNode("div", _hoisted_21, [
                    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "select-area-form-step-icon" }, [
                      _createElementVNode("span", null, "2")
                    ], -1)),
                    _createElementVNode("span", _hoisted_22, _toDisplayString(_unref(_$t)("diagnosisProcess_selectArea_step_city")), 1)
                  ])
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "select-area-form-back-button",
                  onClick: goToStep1
                }, [
                  _createElementVNode("img", {
                    src: "/server/necs/images/icon_arrow_right_EC7400.svg",
                    alt: _unref(_$t)('common_button_back')
                  }, null, 8, _hoisted_23),
                  _createTextVNode(_toDisplayString(_unref(_$t)("diagnosisProcess_selectArea_back_to_prefecture")), 1)
                ]),
                _createElementVNode("ul", _hoisted_24, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cityGroups.value, (cityGroup, index) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: index,
                      class: _normalizeClass(["select-area-form-city-item", { 'is-flat': isFlatCityList.value }])
                    }, [
                      (!isFlatCityList.value && cityGroup.name)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createElementVNode("input", {
                              type: "checkbox",
                              class: "city-item-switch",
                              id: `city-item-switch-${index}`
                            }, null, 8, _hoisted_25),
                            _createElementVNode("label", {
                              class: "city-item-label",
                              for: `city-item-switch-${index}`
                            }, [
                              _createElementVNode("span", null, _toDisplayString(cityGroup.name), 1),
                              _cache[12] || (_cache[12] = _createElementVNode("img", { src: "/server/necs/images/open_24dp_FILL0_wght600_GRAD0_opsz24.png" }, null, -1))
                            ], 8, _hoisted_26)
                          ], 64))
                        : _createCommentVNode("", true),
                      _createElementVNode("ul", {
                        class: "city-item-list",
                        id: `city-item-list-${index}`
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cityGroup.cities, (city) => {
                          return (_openBlock(), _createElementBlock("li", {
                            key: city.value,
                            class: "city-item-row"
                          }, [
                            _withDirectives(_createElementVNode("input", {
                              type: "radio",
                              name: "city-selection",
                              class: "city-item-checkbox",
                              id: `city-item-checkbox-${city.value}`,
                              value: city.value,
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedCityName).value = $event))
                            }, null, 8, _hoisted_28), [
                              [_vModelRadio, selectedCityName.value]
                            ]),
                            _createElementVNode("label", {
                              for: `city-item-checkbox-${city.value}`
                            }, _toDisplayString(city.name), 9, _hoisted_29)
                          ]))
                        }), 128))
                      ], 8, _hoisted_27)
                    ], 2))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_30, [
          _createElementVNode("div", _hoisted_31, [
            _createElementVNode("button", {
              type: "button",
              class: "modal-cansel",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('close')))
            }, _toDisplayString(_unref(_$t)("common_button_cancel")), 1),
            (currentStep.value === 2)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "modal-ok",
                  disabled: !selectedCityName.value,
                  onClick: confirmSelection
                }, _toDisplayString(_unref(_$t)("common_button_confirm")), 9, _hoisted_32))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ]),
    (_unref(errorDialog).show)
      ? (_openBlock(), _createBlock(ErrorDialog, _mergeProps({ key: 0 }, _unref(errorDialog), {
          onOk: _unref(errorDialog).close,
          onCancel: _unref(errorDialog).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})